import { useState } from "react";
import { AlertTriangle, Tag } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/*
 * The "Include offers for everyone" override. This is a deliberate,
 * non-compliant setting: it appends promotional content to order
 * confirmations for customers who never opted in to marketing. It is kept as
 * an owner-controlled override, but turning it ON is a destructive action, so
 * it requires an AlertDialog confirmation and shows the compliance warning as
 * visible text beside the control. Turning it OFF stays one tap.
 */
export const SendOffersToAllRow = ({
  checked,
  onConfirm,
}: {
  checked: boolean;
  onConfirm: (value: boolean) => void;
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleToggle = (next: boolean) => {
    if (next) {
      // Turning it on is the risky direction: confirm first, never write yet.
      setConfirmOpen(true);
    } else {
      onConfirm(false);
    }
  };

  return (
    <div className="flex items-start justify-between gap-4 py-4 first:pt-0 last:pb-0">
      <div className="flex min-w-0 items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <Tag className="h-[18px] w-[18px]" />
        </div>
        <div className="min-w-0">
          <Label
            id="send-offers-to-all-label"
            htmlFor="send-offers-to-all"
            className="text-sm font-medium text-foreground"
          >
            Include offers for everyone
          </Label>
          <p id="send-offers-to-all-desc" className="mt-1 text-xs leading-relaxed text-muted-foreground">
            Add promotional content to confirmation texts for all customers, not just subscribers.
          </p>
          <p className="mt-2 flex items-start gap-1.5 text-xs leading-relaxed text-status-warning">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span>
              Sends offer text with order confirmations to customers who have NOT opted in to marketing. This is not
              TCPA-compliant; use only if you accept that risk.
            </span>
          </p>
        </div>
      </div>
      <Switch
        id="send-offers-to-all"
        checked={checked}
        onCheckedChange={handleToggle}
        aria-labelledby="send-offers-to-all-label"
        aria-describedby="send-offers-to-all-desc"
      />

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send offers to customers who have not opted in?</AlertDialogTitle>
            <AlertDialogDescription>
              This appends promotional content to order confirmations for every customer, including people who never
              opted in to marketing. This is not TCPA-compliant and can carry legal risk. Only turn this on if you
              accept that risk.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep offers for subscribers only</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => onConfirm(true)}
            >
              Turn it on anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SendOffersToAllRow;
